import { date } from 'quasar';
import { defineStore } from 'pinia';
import dateUtils from 'src/utils/date';
import type { dynamicObj } from 'src/pages/assets';
import { taskApiService } from 'src/services';
import { useSettingStore } from 'src/store/main';
import { isUndefinedOrNull, toPositiveInt } from 'src/utils/general';
import { LocalStorage, SessionStorage } from 'quasar';
import { replacer, reviver } from 'src/utils/mapUtil';
import { tasks_notification_setting, CONSTANTS } from 'src/constants';
import { userApiService } from 'src/services';
import { getUserDetailFromkey, isUserAdmin } from 'src/utils/UserDetails';

export interface ColumnWithExtras {
  title: string;
  tasks: any[];
  visibleCount?: number;
  loadingMoreTasks?: boolean;
}

export const useTaskStore = defineStore('tasks_store', {
  state: () => ({
    tasks: [] as dynamicObj[],
    document_related_tasks: [] as dynamicObj[],
    tasks_loading: false,
    total_document_tasks: 0,
    total_records: 0,
    offset: 0,
    page_size: 10,
    processing: false,
    sort_by: 'created_on',
    sort_order: 'desc',
    data_fetched: false,
    status: '',
    taskListSettingsMap: new Map(),
    taskfiltersMap: new Map(),
    tasks_list_settings_key: CONSTANTS.tasks_list_settings,
    columnKey: CONSTANTS.tasks_columns,
    list_mode: false,
    columns: [
      {
        title: 'open',
        tasks: [],
      },
      {
        title: 'in_progress',
        tasks: [],
      },
      {
        title: 'done',
        tasks: [],
      },
      {
        title: 'rejected',
        tasks: [],
      },
    ] as ColumnWithExtras[],

    task: {} as dynamicObj,
    timeOptions: [] as dynamicObj[],
    task_date_format: 'YYYY/MM/DD',
    selectionList: [] as any,
    is_module_on: false,
    statusList: [] as any,
    headerCheckbox: false,
    total_pages: 0,
    current_page: 1,
    show_table_loader: false,
    userGroupIds: [] as any,
    isUserAdmin: isUserAdmin(),
    userId: getUserDetailFromkey('id'),
    list_size_key: 'TASK_PAGINATION_SIZE',
  }),
  getters: {},
  actions: {
    resetColumns() {
      this.columns.forEach((column: any) => {
        column.tasks = [];
      });
    },
    formatTaskList() {
      this.resetColumns();
      this.tasks.forEach((task: any) => {
        const status = task.status;
        this.columns.find((column: any) => {
          if (column.title === status) {
            column.tasks.push(task);
          }
        });
      });
    },
    async saveTaskColumns(columns: dynamicObj) {
      try {
        const settings_store = useSettingStore();
        const result = await settings_store.updateSetting(
          this.columnKey.toUpperCase(),
          JSON.stringify(columns),
        );
        LocalStorage.set(this.columnKey, JSON.stringify(columns));
        settings_store.user_settings[this.columnKey.toUpperCase()] =
          JSON.stringify(columns);
        return result;
      } catch (error) {
        logError('Error saving task columns', error);
      }
    },
    resetTaskStore() {
      this.task = {};
    },

    async getTask(id: string, use_cache = true) {
      try {
        this.processing = true;

        this.task = {};
        const response = await taskApiService.getTaskById(id, use_cache);

        if (response.success === false) {
          throw response;
        }
        this.task = response.data;
        this.afterTaskGet();
      } catch (error) {
        throw error;
      } finally {
        this.processing = false;
      }
    },

    afterTaskGet() {
      if (
        !isUndefinedOrNull(this.task.assigned_to_user_id) &&
        this.task.assigned_to_user_id != 0
      ) {
        this.task['assigned_to_group'] = false;
      } else {
        this.task['assigned_to_group'] = true;
      }
    },

    // will be used from ACL store
    async loadUserAcls() {
      try {
        const response = await userApiService.getUserAcls();
        this.userGroupIds = response.group_permissions.map(
          (group: { group_id: string }) => group.group_id,
        );
      } catch (error) {
        return error;
      }
    },

    prepareOpenTaskParams(document_id: any = '') {
      const params: dynamicObj = {};
      if (document_id && document_id != '') {
        params['document_id'] = document_id;
      }
      params['status'] = 'open,in_progress';
      return params;
    },

    async getOpenTasks(document_id: any = '') {
      const params = this.prepareOpenTaskParams(document_id);
      await this.getTasks(params);
    },

    makeDefaultParams(params: dynamicObj) {
      const skip =
        params.document_id && params.document_id != '' ? 0 : this.offset; // Don't skip if document_id is present
      const limit = params.hasOwnProperty('limit')
        ? params.limit
        : this.page_size;
      const default_params = {
        // Guard against NaN/undefined reaching the API (-> HTTP 422). DOCB-12859.
        // offset 0 is valid (first page); NaN/negative collapse to 0.
        skip: toPositiveInt(skip, 0),
        limit: toPositiveInt(limit, 10),
        sort_by: this.sort_by,
        order: this.sort_order,
      };

      return default_params;
    },

    resetBeforeRequest(params: dynamicObj) {
      this.tasks_loading = true;
      this.data_fetched = false;
      if (params.hasOwnProperty('document_id')) {
        this.total_document_tasks = 0;
      } else {
        this.total_records = 0;
      }
    },

    async getTasks(params: dynamicObj) {
      const default_params = this.makeDefaultParams(params);
      Object.assign(params, default_params);
      // delete key document_id from params
      // delete params.document_id;

      const response = await taskApiService.getTasks(params);
      if (response.success === false) {
        throw response;
      }
      this.updateTaskData(response, params);
    },

    updateTaskData(response: dynamicObj, params: dynamicObj) {
      this.resetBeforeRequest(params);
      const tasks = [] as dynamicObj[];
      const responseData =
        response?.data && typeof response.data === 'object'
          ? response.data
          : {};
      for (const [, item] of Object.entries(responseData)) {
        const record = item as dynamicObj;
        record.done_task_icon = 'radio_button_unchecked';
        record.rowSelection = false;
        record.created_on = dateUtils.methods.formatDateAccordingToPreference(
          record.created_on,
        );
        tasks.push(record);
      }
      const metadata =
        response?.metadata && typeof response.metadata === 'object'
          ? response.metadata
          : {};
      if (metadata.hasOwnProperty('total_results')) {
        if (params.hasOwnProperty('document_id')) {
          this.document_related_tasks = tasks;
          this.total_document_tasks = metadata.total_results;
        } else {
          this.tasks = tasks;
          this.total_records = metadata.total_results;
        }
      }

      this.tasks_loading = false;
      this.data_fetched = true;
    },

    // date Time Utility Functions
    getDisplayDeadline(deadline: string) {
      const dateObj = new Date(deadline + this.getTimeZone());
      const timeZoneOffsetMinutes = dateObj.getTimezoneOffset();
      const adjustedDate = new Date(
        dateObj.getTime() - timeZoneOffsetMinutes * 60000,
      );

      const formated_date = date.formatDate(
        adjustedDate,
        this.task_date_format,
      );
      const formatted_time = this.getDeadlineTime(adjustedDate);
      const formatted_deadline = this.makeDeadline(
        formatted_time,
        formated_date,
      );

      return {
        formated_date,
        formatted_time,
        formatted_deadline,
      };
    },

    getTimeZone() {
      return date.formatDate(new Date(), 'Z');
    },

    makeTimeOptions() {
      this.timeOptions = [];
      for (let hour = 0; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const time = this.formatTime(hour, minute);
          this.timeOptions.push({
            label: time,
            value: time,
          });
        }
      }
    },

    formatTime(hour: number, minute: number) {
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      hour = hour ? hour : 12; // Handle midnight (0:00)
      const new_minute = minute < 10 ? '0' + minute : minute;
      return hour + ':' + new_minute + ' ' + ampm;
    },

    getDeadlineTime(date = null as Date | null) {
      let newTime = new Date();

      if (isUndefinedOrNull(date)) {
        newTime = new Date(new Date().getTime() + 30 * 60000); // 30 minutes in milliseconds
        const roundedMinutes = Math.round(newTime.getMinutes() / 15) * 15;
        newTime.setMinutes(roundedMinutes);
      } else {
        newTime = new Date(date!.getTime());
      }

      let hours = newTime.getHours();
      const minutes = newTime.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // Handle midnight (0:00)
      const new_minutes = minutes < 10 ? '0' + minutes : minutes;
      return hours + ':' + new_minutes + ' ' + ampm;
    },

    getIsoFormatDate(date_to = null as Date | null) {
      if (isUndefinedOrNull(date_to) || date_to!.toString() == 'Invalid Date') {
        date_to = new Date();
      }

      let date_iso_format = '';
      if (date_to != null) {
        date_iso_format = date.formatDate(date_to, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
      }

      return date_iso_format;
    },

    makeDeadline(time: any, date: any) {
      if (time && date) {
        return date + ' ' + time;
      }
    },
    //END date Time Utility Functions

    // Search Utility Functions

    getSavedListSettings() {
      try {
        const value = LocalStorage.getItem(
          this.tasks_list_settings_key,
        ) as string;
        const newValue = JSON.parse(value, reviver);
        if (newValue) {
          this.taskListSettingsMap = newValue;
          this.parseSettings();
        }

        if (isUndefinedOrNull(newValue)) {
          this.setDefaultSettings();
        }
      } catch (err) {}
    },

    parseSettings() {
      if (this.taskListSettingsMap.size == 0) {
        this.setDefaultSettings();
      }

      if (this.taskListSettingsMap.has('filters')) {
        this.taskfiltersMap = this.taskListSettingsMap.get('filters');
      }

      /*if (this.taskListSettingsMap.has('page_size')) {
        this.page_size = this.taskListSettingsMap.get('page_size');
      }*/

      if (this.taskListSettingsMap.has('sort_by')) {
        const savedSortBy = this.taskListSettingsMap.get('sort_by');
        if (savedSortBy) {
          this.sort_by = savedSortBy;
        }
      }

      if (this.taskListSettingsMap.has('sort_order')) {
        const savedSortOrder = this.taskListSettingsMap.get('sort_order');
        if (savedSortOrder) {
          this.sort_order = savedSortOrder;
        }
      }
    },

    resetFilters() {
      this.setDefaultSettings();
      this.saveListSettings();
    },

    deleteItemInFiltersMap(key: string) {
      if (this.taskfiltersMap.has(key)) {
        this.taskfiltersMap.delete(key);
      }

      this.setListSettings('filters', this.taskfiltersMap);
    },

    setItemInFiltersMap(key: string, value: any) {
      this.taskfiltersMap.set(key, value);
      this.setListSettings('filters', this.taskfiltersMap);
    },

    prepareForDocPendingTasks(docId: string) {
      this.getSavedListSettings();
      this.setItemInFiltersMap('keyword', docId);
      this.setItemInFiltersMap('status', 'open,in_progress');
    },

    saveListSettings() {
      const str = JSON.stringify(this.taskListSettingsMap, replacer);
      LocalStorage.set(this.tasks_list_settings_key, str);
    },

    setListSettings(key: string, value: any) {
      if (key === 'filters') {
        this.setFiltersMap(key, value);
      } else {
        this.taskListSettingsMap.set(key, value);
      }

      this.saveListSettings();
    },

    setFiltersMap(key: string, value: any) {
      this.taskfiltersMap = value;

      const hasAll = this.taskfiltersMap.has('all');
      if (hasAll && this.taskfiltersMap.size > 1) {
        this.taskfiltersMap.delete('all');
      }

      if (!hasAll && this.taskfiltersMap.size == 0) {
        this.taskfiltersMap.set('all', {
          label: 'all',
          value: 'all',
        });
      }

      this.taskListSettingsMap.set(key, this.taskfiltersMap);
    },

    setDefaultSettings() {
      this.taskListSettingsMap = new Map();
      this.taskfiltersMap = new Map();
      this.taskfiltersMap.set('all', {
        label: 'all',
        value: 'all',
      });

      this.page_size = 10;
      this.setListSettings('filters', this.taskfiltersMap);
      this.setListSettings('page_size', this.page_size);
      this.setDefaultSort();
    },

    setDefaultSort() {
      this.sort_by = 'created_on';
      this.sort_order = 'desc';
      this.setListSettings('sort_by', this.sort_by);
      this.setListSettings('sort_order', this.sort_order);
    },

    getSavedFiltersAsJson() {
      const excludeKeys = ['all'];
      const params = {} as dynamicObj;
      for (const [key, savedValue] of this.taskfiltersMap.entries()) {
        let value = savedValue;
        if (excludeKeys.includes(key)) {
          continue;
        }

        if (value.hasOwnProperty('value')) {
          value = value.value;
        }

        if (key == 'only_for_current_user') {
          if (value == 'all') {
            continue;
          }
        }

        if (key == 'status') {
          value = this.getStatusValue(value);
        }

        params[key] = value;
      }

      return params;
    },

    getStatusValue(value: any) {
      if (typeof value == 'string') {
        return value;
      }

      if (Array.isArray(value)) {
        const namesArray = value.map((item: any) =>
          typeof item === 'string' ? item : (item?.value as string),
        );
        return namesArray.join(',');
      }

      return '';
    },

    adjustCountForDoneTasks(doneCount: number) {
      if (this.currentViewStillCountsDoneTasks()) {
        return;
      }
      this.total_records = Math.max(0, this.total_records - doneCount);
      this.recalculateTotalPages();
    },

    currentViewStillCountsDoneTasks() {
      const activeStatuses = this.activeStatusFilter();
      return activeStatuses.length === 0 || activeStatuses.includes('done');
    },

    activeStatusFilter() {
      const status = this.taskfiltersMap.get('status');
      if (isUndefinedOrNull(status) || status === '') {
        return [] as string[];
      }
      if (typeof status === 'string') {
        return status.split(',').filter((s: string) => s.length > 0);
      }
      if (Array.isArray(status)) {
        return status
          .map((item: any) => (typeof item === 'string' ? item : item?.value))
          .filter((s: string) => s && s.length > 0);
      }
      return [] as string[];
    },

    recalculateTotalPages() {
      this.total_pages = Math.ceil(this.total_records / this.page_size);
    },

    async setPaginationSize(size: string) {
      this.page_size = toPositiveInt(size, 10);
      this.setListSettings('page_size', this.page_size);

      const settings_store = useSettingStore();
      await settings_store.setUserPreferences(
        this.list_size_key,
        this.page_size ? this.page_size : '10',
      );
    },

    setOrderBy(field: any, direction: string) {
      this.sort_by = field;
      this.sort_order = direction;
      this.setListSettings('sort_by', this.sort_by);
      this.setListSettings('sort_order', this.sort_order);
    },
    // END Search Utility Functions

    async isModuleOn() {
      this.is_module_on = false;
      const settings_store = useSettingStore();
      await settings_store.startSettingsFetch();
      const { user_settings } = settings_store;

      if (
        user_settings[tasks_notification_setting] &&
        user_settings[tasks_notification_setting] === true
      ) {
        this.is_module_on = true;
      }

      return this.is_module_on;
    },

    // Module Setting Functions

    // will be used from ACL store
    isHasAccess(row: any) {
      return (
        !this.isUserAdmin &&
        row.assigned_to_user_id !== this.userId &&
        !this.userGroupIds.includes(row.assigned_to_group_id)
      );
    },
  },
});
